import { useState, useCallback, useEffect } from 'react';
import api from '@/api';
import {
  Upload, FileText, Users, AlertTriangle, CheckCircle2, Loader2,
  ArrowRight, Sparkles, Building2, Wrench, Shield, ClipboardList,
  XCircle, FileSearch,
  ArrowRightLeft, ScanLine, Layers, ReceiptText, FolderUp, X, RotateCcw
} from 'lucide-react';
import { TimelineSummaryCompact } from './TimelinePanel';
import { useDrawingExtractionProgress, useIntakeProgress } from '@/hooks/useExtractionProgress';

const docTypeConfig = {
  subcontractor_list: { label: 'Subcontractor List', icon: Users, color: 'text-blue-700 dark:text-blue-400', action: 'Parse Sub List' },
  rfi_log: { label: 'RFI Log', icon: AlertTriangle, color: 'text-amber-700 dark:text-amber-400', action: 'Parse RFI Log' },
  g703_sov: { label: 'G703 Schedule of Values', icon: FileText, color: 'text-primary', action: 'Import G703' },
  equipment_schedule: { label: 'Equipment Schedule', icon: Wrench, color: 'text-cyan-700 dark:text-cyan-400', action: 'Extract Equipment' },
  drawing_set: { label: 'Drawing Set', icon: ScanLine, color: 'text-emerald-700 dark:text-emerald-400', action: 'Process Drawings' },
  submittal: { label: 'Submittal', icon: ClipboardList, color: 'text-violet-700 dark:text-violet-400', action: 'Classify & File' },
  warranty_certificate: { label: 'Warranty Certificate', icon: Shield, color: 'text-primary', action: 'Extract Warranty' },
  specifications: { label: 'Specifications', icon: FileText, color: 'text-muted-foreground', action: 'Classify & File' },
  other: { label: 'Other Document', icon: FileText, color: 'text-muted-foreground', action: 'Classify & File' },
};

export const PHASE_LABELS = {
  scanning: 'Scanning',
  downloading: 'Downloading',
  rendering: 'Rendering',
  prescanning: 'Pre-Scan',
  analyzing: 'AI Analysis',
  creating: 'Creating',
  deduplicating: 'Deduplicating',
  classifying: 'Classifying',
  complete: 'Complete',
  error: 'Error',
};

export const PHASE_COLORS = {
  scanning: 'text-blue-700 dark:text-blue-400',
  downloading: 'text-cyan-700 dark:text-cyan-400',
  rendering: 'text-amber-700 dark:text-amber-400',
  prescanning: 'text-indigo-700 dark:text-indigo-400',
  analyzing: 'text-violet-700 dark:text-violet-400',
  creating: 'text-emerald-700 dark:text-emerald-400',
  deduplicating: 'text-orange-700 dark:text-orange-400',
  classifying: 'text-teal-700 dark:text-teal-400',
  complete: 'text-emerald-700 dark:text-emerald-400',
  error: 'text-red-700 dark:text-red-400',
};

export const INTAKE_PHASE_LABELS = {
  extracting: 'Extracting Text',
  rendering: 'Rendering Pages',
  compressing: 'Compressing',
  classifying: 'AI Classifying',
  complete: 'Complete',
  error: 'Error',
};

export const INTAKE_PHASE_COLORS = {
  extracting: 'text-blue-700 dark:text-blue-400',
  rendering: 'text-cyan-700 dark:text-cyan-400',
  compressing: 'text-amber-700 dark:text-amber-400',
  classifying: 'text-violet-700 dark:text-violet-400',
  complete: 'text-emerald-700 dark:text-emerald-400',
  error: 'text-red-700 dark:text-red-400',
};


export default function IntakePanel({ projectId }) {
  const [mode, setMode] = useState('upload'); // upload | identify | sublist | rfilog
  const [uploading, setUploading] = useState(false);
  const [identification, setIdentification] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // SSE progress — extracted into reusable hooks
  const {
    drawingProgress, drawingExtracting, completionResult,
    startTracking: startDrawingTracking, reset: resetDrawing
  } = useDrawingExtractionProgress(projectId);

  const { intakeProgress, reset: resetIntakeProgress } = useIntakeProgress(projectId, uploading);

  // Handle drawing extraction completion from hook
  useEffect(() => {
    if (!completionResult) return;
    if (completionResult.status === 'done') {
      setResults({ type: 'drawing', data: completionResult.data });
    } else if (completionResult.status === 'error') {
      setError(completionResult.data?.message || 'Drawing extraction failed');
    }
    setProcessing(false);
  }, [completionResult]);

  // Batch upload queue
  const [uploadQueue, setUploadQueue] = useState([]);     // [{file, status, result, error}]
  const [currentQueueIdx, setCurrentQueueIdx] = useState(-1);
  const [dragActive, setDragActive] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Fetch intake analytics
  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/intake-analytics`)
      .then(r => setAnalytics(r.data))
      .catch(() => {});
  }, [projectId, uploading]); // refresh after upload completes

  // --- Chunked upload helper ---
  // Production ingress caps request bodies at 100MB. Large files are sliced
  // into 8MB chunks (also under the 16MB MongoDB document limit) and
  // reassembled server-side. Smaller files use the direct single-request path.
  const CHUNK_SIZE = 8 * 1024 * 1024;       // 8MB per chunk
  const CHUNK_THRESHOLD = 25 * 1024 * 1024; // files larger than this are chunked

  const uploadIntake = async (file, onProgress) => {
    if (file.size <= CHUNK_THRESHOLD) {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/closeout/projects/${projectId}/intake`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      return res.data;
    }

    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', i);
      fd.append('total_chunks', totalChunks);
      fd.append('file', blob, file.name);
      await api.post(`/closeout/projects/${projectId}/intake/chunk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    const ff = new FormData();
    ff.append('upload_id', uploadId);
    ff.append('filename', file.name);
    ff.append('total_chunks', totalChunks);
    const res = await api.post(`/closeout/projects/${projectId}/intake/finalize`, ff, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return res.data;
  };

  // Batch queue processor — process next file when current completes
  useEffect(() => {
    if (uploadQueue.length === 0 || currentQueueIdx < 0) return;
    if (currentQueueIdx >= uploadQueue.length) {
      setCurrentQueueIdx(-1);
      return;
    }
    const item = uploadQueue[currentQueueIdx];
    if (item.status !== 'pending') return;

    const processFile = async () => {
      setUploadQueue(q => q.map((f, i) => i === currentQueueIdx ? { ...f, status: 'uploading' } : f));
      setUploading(true);

      try {
        const data = await uploadIntake(item.file, (pct) => {
          setUploadQueue(q => q.map((f, i) => i === currentQueueIdx ? { ...f, uploadPct: pct } : f));
        });
        setUploadQueue(q => q.map((f, i) => i === currentQueueIdx ? { ...f, status: 'done', result: data } : f));
      } catch (err) {
        setUploadQueue(q => q.map((f, i) => i === currentQueueIdx
          ? { ...f, status: 'error', error: err.response?.data?.detail || (err.response?.status === 413 ? 'File too large' : 'Upload failed') } : f));
      } finally {
        setUploading(false);
        setCurrentQueueIdx(prev => prev + 1);
      }
    };
    processFile();
  }, [currentQueueIdx, uploadQueue, projectId]);

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragActive(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setDragActive(false); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf','xlsx','xls','csv','png','jpg','jpeg','docx'].includes(ext);
    });
    if (files.length === 0) return;
    if (files.length === 1) {
      // Single file → regular smart upload flow
      setSelectedFile(files[0]);
      processSingleSmartUpload(files[0]);
    } else {
      // Multiple files → batch queue
      startBatchUpload(files);
    }
  }, []);

  const startBatchUpload = (files) => {
    setError(null);
    setResults(null);
    setIdentification(null);
    const queue = files.map(f => ({ file: f, status: 'pending', result: null, error: null }));
    setUploadQueue(queue);
    setCurrentQueueIdx(0);
    setMode('batch');
  };

  const clearQueue = () => {
    setUploadQueue([]);
    setCurrentQueueIdx(-1);
    setMode('upload');
  };

  const removeQueueItem = (idx) => {
    // Only allow removing items that are not currently uploading
    setUploadQueue(q => {
      if (q[idx]?.status === 'uploading') return q;
      const next = q.filter((_, i) => i !== idx);
      if (next.length === 0) {
        setCurrentQueueIdx(-1);
        setMode('upload');
      }
      return next;
    });
  };

  const retryQueueItem = (idx) => {
    setUploadQueue(q => q.map((f, i) =>
      i === idx ? { ...f, status: 'pending', error: null, result: null } : f
    ));
    setCurrentQueueIdx(idx);
  };

  const processSingleSmartUpload = async (file) => {
    setUploading(true);
    setError(null);
    setIdentification(null);
    setResults(null);

    try {
      const data = await uploadIntake(file);
      setIdentification(data);
      setMode('identify');
    } catch (err) {
      setError(err.response?.data?.detail || (err.response?.status === 413 ? 'File too large' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const resetState = () => {
    setIdentification(null);
    setResults(null);
    setError(null);
    setSelectedFile(null);
    setMode('upload');
    resetDrawing();
    resetIntakeProgress();
    setUploadQueue([]);
    setCurrentQueueIdx(-1);
  };

  // Smart Intake — auto-identify any document (supports single + multiple files)
  const handleSmartUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    e.target.value = '';

    if (files.length === 1) {
      setSelectedFile(files[0]);
      processSingleSmartUpload(files[0]);
    } else {
      startBatchUpload(files);
    }
  };

  // Direct sub-list upload
  const handleSubListUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    setError(null);
    setResults(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('create_divisions', 'true');
      fd.append('create_stakeholders', 'true');
      const res = await api.post(`/closeout/projects/${projectId}/intake/sub-list`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResults({ type: 'sublist', data: res.data });
      setMode('upload');
    } catch (err) {
      setError(err.response?.data?.detail || 'Sub list parse failed');
    } finally {
      setProcessing(false);
      e.target.value = '';
    }
  };

  // Direct RFI log upload
  const handleRfiUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    setError(null);
    setResults(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('apply_equipment_changes', 'true');
      const res = await api.post(`/closeout/projects/${projectId}/intake/rfi-log`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResults({ type: 'rfi', data: res.data });
      setMode('upload');
    } catch (err) {
      setError(err.response?.data?.detail || 'RFI parse failed');
    } finally {
      setProcessing(false);
      e.target.value = '';
    }
  };

  // Drawing set upload (large file, background processing with SSE progress)
  const handleDrawingUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setProcessing(true);
    setError(null);
    setResults(null);

    try {
      // Step 1: Upload via Smart Intake (stores file, shows intake classification progress)
      setUploading(true);
      const uploadData = await uploadIntake(file);
      const intakeId = uploadData.intake_id;
      setUploading(false);

      // Step 2: Trigger drawing processing using intake_id (no re-upload)
      startDrawingTracking();
      const fd = new FormData();
      fd.append('intake_id', intakeId);
      await api.post(`/closeout/projects/${projectId}/intake/drawing-set`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      // SSE handles completion from here
    } catch (err) {
      setError(err.response?.data?.detail || 'Drawing upload failed');
      setProcessing(false);
      resetDrawing();
      setUploading(false);
    }
  };

  // Change Order / VE Log upload
  const handleChangeOrderUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    setError(null);
    setResults(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('apply_equipment_changes', 'true');
      const res = await api.post(`/closeout/projects/${projectId}/intake/change-orders`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResults({ type: 'changeorder', data: res.data });
      setMode('upload');
    } catch (err) {
      setError(err.response?.data?.detail || 'Change order parse failed');
    } finally {
      setProcessing(false);
      e.target.value = '';
    }
  };

  // Execute the suggested action from smart intake
  const executeAction = async () => {
    if (!identification) return;
    const docType = identification.identification?.document_type;
    const intakeId = identification.intake_id;
    setProcessing(true);
    setError(null);

    try {
      if (docType === 'subcontractor_list') {
        const fd = new FormData();
        if (intakeId) fd.append('intake_id', intakeId);
        else if (selectedFile) fd.append('file', selectedFile);
        fd.append('create_divisions', 'true');
        fd.append('create_stakeholders', 'true');
        const res = await api.post(`/closeout/projects/${projectId}/intake/sub-list`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        });
        setResults({ type: 'sublist', data: res.data });
      } else if (docType === 'rfi_log') {
        const fd = new FormData();
        if (intakeId) fd.append('intake_id', intakeId);
        else if (selectedFile) fd.append('file', selectedFile);
        fd.append('apply_equipment_changes', 'true');
        const res = await api.post(`/closeout/projects/${projectId}/intake/rfi-log`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        });
        setResults({ type: 'rfi', data: res.data });
      } else if (docType === 'drawing_set') {
        startDrawingTracking();
        const fd = new FormData();
        if (intakeId) fd.append('intake_id', intakeId);
        else if (selectedFile) fd.append('file', selectedFile);
        await api.post(`/closeout/projects/${projectId}/intake/drawing-set`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        });
        setMode('upload');
        return;
      } else if (docType === 'change_order_log') {
        const fd = new FormData();
        if (intakeId) fd.append('intake_id', intakeId);
        else if (selectedFile) fd.append('file', selectedFile);
        fd.append('apply_equipment_changes', 'true');
        const res = await api.post(`/closeout/projects/${projectId}/intake/change-orders`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        });
        setResults({ type: 'change_order', data: res.data });
      } else {
        setResults({ type: 'info', data: { message: `Document identified as "${docType}". Use the corresponding tab to process.` } });
      }
      setMode('upload');
    } catch (err) {
      setError(err.response?.data?.detail || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const dtConf = identification?.identification?.document_type
    ? docTypeConfig[identification.identification.document_type] || docTypeConfig.other
    : docTypeConfig.other;
  const DtIcon = dtConf.icon;

  return (
    <div data-testid="intake-panel" className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-foreground">
            Smart Intake
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload any construction document — AI identifies it and routes it to the right parser
          </p>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {analytics && (analytics.total_documents > 0 || analytics.assets_extracted > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="intake-analytics-dashboard">
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Documents</p>
            <p className="font-mono text-lg font-bold text-foreground mt-0.5">{analytics.total_documents}</p>
            <p className="text-[9px] text-muted-foreground">{analytics.total_pages} pages</p>
          </div>
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Drawing Sets</p>
            <p className="font-mono text-lg font-bold text-cyan-600 dark:text-cyan-400 mt-0.5">{analytics.drawing_sets}</p>
            <p className="text-[9px] text-muted-foreground">{analytics.sheets} sheets</p>
          </div>
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Extracted</p>
            <p className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{analytics.assets_extracted}</p>
            <p className="text-[9px] text-muted-foreground">assets found</p>
          </div>
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">AI Jobs</p>
            <p className="font-mono text-lg font-bold text-violet-600 dark:text-violet-400 mt-0.5">{analytics.jobs_completed}/{analytics.extraction_jobs}</p>
            <p className="text-[9px] text-muted-foreground">{analytics.total_pages_scanned} pages scanned</p>
          </div>
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Status</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(analytics.status_breakdown || {}).map(([s, c]) => (
                <span key={s} className={`text-[8px] font-mono px-1 py-0 rounded-sm border ${
                  s === 'processed' || s === 'accepted' ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' :
                  s === 'error' || s === 'failed' ? 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800' :
                  'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                }`}>{s} {c}</span>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm px-3 py-2.5">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Doc Types</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(analytics.type_breakdown || {}).slice(0, 4).map(([t, c]) => (
                <span key={t} className="text-[8px] font-mono px-1 py-0 rounded-sm border border-border text-muted-foreground">
                  {t.replace(/_/g, ' ')} {c}
                </span>
              ))}
              {Object.keys(analytics.type_breakdown || {}).length > 4 && (
                <span className="text-[8px] text-muted-foreground/50">+{Object.keys(analytics.type_breakdown).length - 4}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Options — Drag-and-drop zone */}
      <div
        data-testid="intake-dropzone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative rounded-sm transition-all ${
          dragActive
            ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background bg-orange-50 dark:bg-orange-950/10'
            : ''
        }`}
      >
        {dragActive && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/90 rounded-sm pointer-events-none">
            <div className="flex flex-col items-center gap-2">
              <FolderUp className="w-10 h-10 text-primary animate-bounce" />
              <p className="text-sm font-heading text-primary uppercase tracking-wider">Drop files here</p>
              <p className="text-xs text-muted-foreground">Multiple files will be queued for batch processing</p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-5 gap-3">

        {/* Smart Upload */}
        <label className="group cursor-pointer" data-testid="smart-upload-btn">
          <div className="flex flex-col items-center gap-2 p-5 bg-card border border-dashed border-primary/30 rounded-sm hover:bg-orange-50 dark:hover:bg-orange-950/10 hover:border-blue-500/50 transition-all">
            {uploading ? <Loader2 className="w-6 h-6 text-primary animate-spin" /> : <Sparkles className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />}
            <p className="text-xs font-heading uppercase tracking-wider text-primary">Smart Upload</p>
            <p className="text-[10px] text-muted-foreground text-center">AI auto-identifies any document type</p>
          </div>
          <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.docx" multiple onChange={handleSmartUpload} disabled={uploading} />
        </label>

        {/* Sub List Upload */}
        <label className="group cursor-pointer" data-testid="sublist-upload-btn">
          <div className="flex flex-col items-center gap-2 p-5 bg-card border border-dashed border-blue-300 dark:border-blue-800/40 rounded-sm hover:bg-blue-50 dark:hover:bg-blue-950/10 hover:border-blue-500 dark:hover:border-blue-700/60 transition-all">
            {processing && mode === 'sublist' ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin" /> : <Users className="w-6 h-6 text-blue-500 group-hover:scale-110 transition-transform" />}
            <p className="text-xs font-heading uppercase tracking-wider text-blue-700 dark:text-blue-400">Subcontractor List</p>
            <p className="text-[10px] text-muted-foreground text-center">Excel/CSV → creates trades & stakeholders</p>
          </div>
          <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => { setMode('sublist'); handleSubListUpload(e); }} disabled={processing} />
        </label>

        {/* RFI Log Upload */}
        <label className="group cursor-pointer" data-testid="rfi-upload-btn">
          <div className="flex flex-col items-center gap-2 p-5 bg-card border border-dashed border-amber-300 dark:border-amber-800/40 rounded-sm hover:bg-amber-50 dark:hover:bg-amber-950/10 hover:border-amber-500 dark:hover:border-amber-700/60 transition-all">
            {processing && mode === 'rfilog' ? <Loader2 className="w-6 h-6 text-amber-500 animate-spin" /> : <AlertTriangle className="w-6 h-6 text-amber-500 group-hover:scale-110 transition-transform" />}
            <p className="text-xs font-heading uppercase tracking-wider text-amber-700 dark:text-amber-400">RFI Log</p>
            <p className="text-[10px] text-muted-foreground text-center">Parses RFIs & tracks equipment changes</p>
          </div>
          <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv" onChange={(e) => { setMode('rfilog'); handleRfiUpload(e); }} disabled={processing} />
        </label>

        {/* Drawing Set Upload */}
        <label className="group cursor-pointer" data-testid="drawing-upload-btn">
          <div className="flex flex-col items-center gap-2 p-5 bg-card border border-dashed border-emerald-300 dark:border-emerald-800/40 rounded-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/10 hover:border-emerald-500 dark:hover:border-emerald-700/60 transition-all">
            {processing && mode === 'drawing' ? <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" /> : <Layers className="w-6 h-6 text-emerald-500 group-hover:scale-110 transition-transform" />}
            <p className="text-xs font-heading uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Drawing Set</p>
            <p className="text-[10px] text-muted-foreground text-center">Extracts all equipment from MEP drawings</p>
          </div>
          <input type="file" className="hidden" accept=".pdf" onChange={(e) => { setMode('drawing'); handleDrawingUpload(e); }} disabled={processing} />
        </label>

        {/* Change Order / VE Log Upload */}
        <label className="group cursor-pointer" data-testid="co-upload-btn">
          <div className="flex flex-col items-center gap-2 p-5 bg-card border border-dashed border-rose-300 dark:border-rose-800/40 rounded-sm hover:bg-rose-50 dark:hover:bg-rose-950/10 hover:border-rose-500 dark:hover:border-rose-700/60 transition-all">
            {processing && mode === 'co' ? <Loader2 className="w-6 h-6 text-rose-500 animate-spin" /> : <ReceiptText className="w-6 h-6 text-rose-500 group-hover:scale-110 transition-transform" />}
            <p className="text-xs font-heading uppercase tracking-wider text-rose-700 dark:text-rose-400">Change Orders</p>
            <p className="text-[10px] text-muted-foreground text-center">VE log → flags equipment changes</p>
          </div>
          <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv" onChange={(e) => { setMode('co'); handleChangeOrderUpload(e); }} disabled={processing} />
        </label>
        </div>
      </div>

      {/* Intake Classification Progress (SSE-driven) */}
      {uploading && intakeProgress && (
        <div className="p-4 bg-card border border-primary/30 rounded-sm space-y-3" data-testid="intake-classification-progress">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs font-heading uppercase tracking-wider text-primary">Smart Classification</span>
            </div>
            <span className={`text-xs font-heading uppercase tracking-wider ${INTAKE_PHASE_COLORS[intakeProgress.phase] || 'text-muted-foreground'}`}>
              {INTAKE_PHASE_LABELS[intakeProgress.phase] || intakeProgress.phase}
            </span>
          </div>

          <div className="space-y-1.5">
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${intakeProgress.pct || 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-muted-foreground">{intakeProgress.message}</span>
              <span className="font-mono text-primary">{intakeProgress.pct || 0}%</span>
            </div>
          </div>

          {intakeProgress.pages > 0 && (
            <div className="text-[10px] text-muted-foreground">
              Pages in document: <span className="font-mono text-foreground/80">{intakeProgress.pages}</span>
            </div>
          )}

          <div className="flex items-center gap-1 mt-1">
            {['extracting', 'rendering', 'classifying'].map((phase, idx) => {
              const isActive = intakeProgress.phase === phase;
              const isPast = ['extracting', 'rendering', 'classifying'].indexOf(intakeProgress.phase) > idx;
              return (
                <div key={phase} className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full transition-all ${
                    isActive ? 'bg-amber-400 animate-pulse scale-125' :
                    isPast ? 'bg-orange-600' : 'bg-slate-700'
                  }`} />
                  <span className={`text-[9px] uppercase tracking-wider ${
                    isActive ? 'text-primary font-bold' :
                    isPast ? 'text-orange-700' : 'text-muted-foreground'
                  }`}>{INTAKE_PHASE_LABELS[phase]?.split(' ')[1] || phase}</span>
                  {idx < 2 && <span className="text-muted-foreground text-[8px] mx-0.5">&rarr;</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Batch Upload Queue */}
      {mode === 'batch' && uploadQueue.length > 0 && (
        <div className="p-4 bg-card border border-primary/30 rounded-sm space-y-3" data-testid="batch-queue">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderUp className="w-4 h-4 text-primary" />
              <span className="text-xs font-heading uppercase tracking-wider text-primary">
                Batch Upload — {uploadQueue.filter(f => f.status === 'done').length}/{uploadQueue.length} complete
              </span>
            </div>
            <button onClick={clearQueue} className="text-muted-foreground hover:text-muted-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.round((uploadQueue.filter(f => f.status === 'done').length / uploadQueue.length) * 100)}%` }}
            />
          </div>

          {/* Per-file status */}
          <div className="max-h-[240px] overflow-y-auto space-y-1">
            {uploadQueue.map((item, i) => {
              const dtConf2 = item.result?.identification?.document_type
                ? docTypeConfig[item.result.identification.document_type] || docTypeConfig.other
                : null;
              return (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-sm text-xs border ${
                  item.status === 'uploading' ? 'bg-orange-50 dark:bg-orange-950/10 border-primary/30' :
                  item.status === 'done' ? 'bg-emerald-50 dark:bg-emerald-950/10 border-emerald-300 dark:border-emerald-900/20' :
                  item.status === 'error' ? 'bg-red-50 dark:bg-red-950/10 border-red-300 dark:border-red-900/20' :
                  'bg-secondary/20 border-border/30'
                }`}>
                  {item.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />}
                  {item.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                  {item.status === 'error' && <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                  {item.status === 'pending' && <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}

                  <span className={`flex-1 truncate ${
                    item.status === 'uploading' ? 'text-primary' :
                    item.status === 'done' ? 'text-foreground/80' :
                    item.status === 'error' ? 'text-red-300' : 'text-muted-foreground'
                  }`}>
                    {item.file.name}
                  </span>

                  {item.status === 'uploading' && (
                    <span className="text-[10px] font-mono text-primary">
                      {item.uploadPct != null && item.uploadPct < 100
                        ? `${item.uploadPct}%`
                        : (intakeProgress ? `${intakeProgress.pct || 0}%` : '')}
                    </span>
                  )}
                  {item.status === 'done' && dtConf2 && (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-muted/60 ${dtConf2.color}`}>
                      {dtConf2.label}
                    </span>
                  )}
                  {item.status === 'done' && item.result?.identification?.confidence && (
                    <span className="text-[10px] font-mono text-emerald-600">
                      {Math.round(item.result.identification.confidence * 100)}%
                    </span>
                  )}
                  {item.status === 'error' && (
                    <span className="text-[10px] text-red-500 truncate max-w-[120px]">{item.error}</span>
                  )}

                  {/* Per-item retry (error) / remove (not uploading) */}
                  {item.status === 'error' && (
                    <button
                      onClick={() => retryQueueItem(i)}
                      data-testid={`queue-retry-${i}`}
                      title="Retry upload"
                      className="shrink-0 p-0.5 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {item.status !== 'uploading' && (
                    <button
                      onClick={() => removeQueueItem(i)}
                      data-testid={`queue-remove-${i}`}
                      title="Remove from queue"
                      className="shrink-0 p-0.5 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {uploadQueue.every(f => f.status === 'done' || f.status === 'error') && (
            <div className="flex gap-2 mt-2">
              <button onClick={clearQueue}
                className="px-3 py-2 text-xs text-muted-foreground border border-border rounded-sm hover:border-slate-500 transition-colors">
                Clear Queue
              </button>
            </div>
          )}
        </div>
      )}

      {/* AI Identification Result */}
      {mode === 'identify' && identification && (
        <div className="p-4 bg-card border border-primary/30 rounded-sm space-y-3" data-testid="identification-result">
          <div className="flex items-center gap-3">
            <DtIcon className={`w-5 h-5 ${dtConf.color}`} />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{dtConf.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{identification.identification?.summary}</p>
            </div>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-amber-100 dark:bg-orange-950/40 border border-amber-300 dark:border-primary/30/40 text-amber-700 dark:text-primary">
              {Math.round((identification.identification?.confidence || 0) * 100)}% match
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={executeAction}
              disabled={processing}
              data-testid="execute-action-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors disabled:opacity-50"
            >
              {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              {processing ? 'Processing...' : dtConf.action}
            </button>
            <button onClick={resetState} className="px-3 py-2 text-xs text-muted-foreground border border-border rounded-sm hover:border-slate-500 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Drawing Extraction Progress (SSE-driven) */}
      {drawingExtracting && drawingProgress && (
        <div className="p-4 bg-card border border-emerald-900/30 rounded-sm space-y-3" data-testid="drawing-progress">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-heading uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Drawing Extraction</span>
            </div>
            <span className={`text-xs font-heading uppercase tracking-wider ${PHASE_COLORS[drawingProgress.phase] || 'text-muted-foreground'}`}>
              {PHASE_LABELS[drawingProgress.phase] || drawingProgress.phase}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1.5">
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${drawingProgress.pct || 0}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-muted-foreground">{drawingProgress.message}</span>
              <span className="font-mono text-emerald-500">{drawingProgress.pct || 0}%</span>
            </div>
          </div>

          {/* Stats row — phase-aware counters */}
          <div className="flex gap-4 text-[10px] flex-wrap">
            {drawingProgress.current_batch > 0 && drawingProgress.total_batches > 0 && (
              <span className="text-muted-foreground">
                Batch: <span className="font-mono text-foreground/80">{drawingProgress.current_batch}/{drawingProgress.total_batches}</span>
              </span>
            )}
            {drawingProgress.total_pages > 0 && (
              <span className="text-muted-foreground">
                Pages: <span className="font-mono text-foreground/80">{drawingProgress.current_page || '...'}/{drawingProgress.total_pages}</span>
              </span>
            )}
            {drawingProgress.items_found > 0 && (
              <span className="text-muted-foreground">
                Items found: <span className="font-mono text-emerald-600 dark:text-emerald-400">{drawingProgress.items_found}</span>
              </span>
            )}
          </div>

          {/* Phase pipeline indicator */}
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {['scanning', 'downloading', 'rendering', 'prescanning', 'analyzing', 'creating', 'deduplicating', 'classifying'].map((phase, idx, arr) => {
              const isActive = drawingProgress.phase === phase;
              const phaseOrder = arr.indexOf(drawingProgress.phase);
              const isPast = phaseOrder > idx;
              return (
                <div key={phase} className="flex items-center gap-0.5">
                  <div className={`w-2 h-2 rounded-full transition-all ${
                    isActive ? 'bg-emerald-400 animate-pulse scale-125' :
                    isPast ? 'bg-emerald-600' : 'bg-slate-400 dark:bg-slate-700'
                  }`} />
                  <span className={`text-[9px] uppercase tracking-wider ${
                    isActive ? 'text-emerald-600 dark:text-emerald-400 font-bold' :
                    isPast ? 'text-emerald-700 dark:text-emerald-600' : 'text-muted-foreground'
                  }`}>{PHASE_LABELS[phase]?.split(' ')[0]}</span>
                  {idx < arr.length - 1 && <span className="text-muted-foreground text-[8px] mx-0.5">&rarr;</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Processing Indicator (non-drawing) */}
      {processing && !drawingExtracting && (
        <div className="flex items-center gap-3 p-3 bg-secondary/20 rounded-sm border border-border/50" data-testid="processing-indicator">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
          <p className="text-xs text-muted-foreground">AI is analyzing the document... This may take 15-30 seconds.</p>
        </div>
      )}

      {/* Drawing upload — waiting for SSE to start (before first progress event) */}
      {drawingExtracting && !drawingProgress && (
        <div className="flex items-center gap-3 p-3 bg-emerald-100 dark:bg-emerald-950/20 rounded-sm border border-emerald-300 dark:border-emerald-900/30" data-testid="drawing-uploading">
          <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
          <p className="text-xs text-emerald-700 dark:text-emerald-400">Starting drawing extraction... Waiting for progress events.</p>
          <p className="text-[10px] text-muted-foreground mt-1">This may take several minutes for large drawing sets.</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-950/20 border border-red-300 dark:border-red-900/30 rounded-sm" data-testid="intake-error">
          <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-700 dark:text-red-600 hover:text-red-500 dark:hover:text-red-400 text-xs">Dismiss</button>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4" data-testid="intake-results">

          {/* Sub List Results */}
          {results.type === 'sublist' && (
            <div className="p-4 bg-card border border-blue-300 dark:border-blue-900/30 rounded-sm space-y-3">
              <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-blue-700 dark:text-blue-400">
                <CheckCircle2 className="w-4 h-4" />
                Subcontractor List Parsed
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-blue-600 dark:text-blue-400">{results.data.subs_parsed}</p>
                  <p className="text-[10px] text-muted-foreground">Subs Found</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">{results.data.divisions_created}</p>
                  <p className="text-[10px] text-muted-foreground">Divisions Created</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-violet-600 dark:text-violet-400">{results.data.stakeholders_created}</p>
                  <p className="text-[10px] text-muted-foreground">Stakeholders Created</p>
                </div>
              </div>
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {results.data.parsed_data?.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 px-2 py-1.5 text-xs bg-secondary/20 rounded-sm border border-border/30">
                    <span className="font-mono text-muted-foreground w-16 shrink-0">{s.contract_number || '—'}</span>
                    <span className="text-foreground/80 flex-1 truncate">{s.company_name}</span>
                    <span className="text-muted-foreground w-24 truncate">{s.trade_inferred}</span>
                    <span className="font-mono text-muted-foreground text-[10px] w-14">{s.csi_number || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RFI Results */}
          {results.type === 'rfi' && (
            <div className="p-4 bg-card border border-amber-900/30 rounded-sm space-y-3">
              <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-amber-700 dark:text-amber-400">
                <CheckCircle2 className="w-4 h-4" />
                RFI Log Parsed
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-amber-600 dark:text-amber-400">{results.data.rfis_parsed}</p>
                  <p className="text-[10px] text-muted-foreground">RFIs Parsed</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-primary">{results.data.equipment_changes_found}</p>
                  <p className="text-[10px] text-muted-foreground">Equipment Changes</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">{results.data.equipment_changes_applied}</p>
                  <p className="text-[10px] text-muted-foreground">Changes Applied</p>
                </div>
              </div>
              {/* Equipment Changes */}
              {results.data.equipment_changes?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-primary">Equipment Changes Found</p>
                  {results.data.equipment_changes.map((c, i) => (
                    <div key={i} className="px-3 py-2 bg-orange-50 dark:bg-orange-950/10 border border-orange-200 dark:border-primary/30/20 rounded-sm text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowRightLeft className="w-3 h-3 text-primary" />
                        <span className="font-medium text-foreground/80">
                          RFI #{c.rfi_number} — {c.change_type}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{c.rfi_date}</span>
                      </div>
                      {c.original_item && <p className="text-muted-foreground pl-5">From: {c.original_item}</p>}
                      {c.new_item && <p className="text-emerald-500 pl-5">To: {c.new_item}</p>}
                      <p className="text-muted-foreground pl-5">{c.description}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* RFI List */}
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {results.data.rfis?.map((r, i) => (
                  <div key={i} className={`flex items-center gap-3 px-2 py-1.5 text-xs rounded-sm border ${
                    r.equipment_change ? 'bg-orange-950/10 border-primary/30/20' : 'bg-secondary/20 border-border/30'
                  }`}>
                    <span className="font-mono text-muted-foreground w-10 shrink-0">#{r.rfi_number}</span>
                    <span className={`flex-1 truncate ${r.equipment_change ? 'text-primary' : 'text-foreground/80'}`}>
                      {r.subject}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{r.trade}</span>
                    {r.equipment_change && (
                      <span className="text-[9px] font-mono px-1.5 py-0.5 bg-orange-950/40 text-primary border border-primary/30 rounded-sm">
                        {r.equipment_change.change_type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Results */}
          {results.type === 'info' && (
            <div className="p-3 bg-secondary/20 border border-border/50 rounded-sm text-xs text-muted-foreground">
              {results.data.message}
            </div>
          )}

          {/* Drawing Extraction Results */}
          {results.type === 'drawing' && (
            <div className="p-4 bg-card border border-emerald-300 dark:border-emerald-900/30 rounded-sm space-y-3" data-testid="drawing-results">
              <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                Drawing Equipment Extraction Complete
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">{results.data.assets_created || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Assets Created</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-blue-600 dark:text-blue-400">{results.data.total_found || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Total Extracted</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-cyan-600 dark:text-cyan-400">{results.data.unique_items || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Unique Items</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-muted-foreground">{results.data.duplicates_skipped || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Duplicates Skipped</p>
                </div>
              </div>

              {/* Division Breakdown */}
              {results.data.division_breakdown?.length > 0 && (
                <div className="space-y-2" data-testid="division-breakdown">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-emerald-600">Breakdown by Trade</p>
                  <div className="grid gap-1.5">
                    {results.data.division_breakdown.map((div, i) => {
                      const tradeColors = {
                        'HVAC': 'border-cyan-300 dark:border-cyan-800/40 text-cyan-700 dark:text-cyan-400',
                        'Electrical': 'border-amber-300 dark:border-amber-800/40 text-amber-700 dark:text-amber-400',
                        'Plumbing': 'border-blue-300 dark:border-blue-800/40 text-blue-700 dark:text-blue-400',
                        'Fire Protection': 'border-red-300 dark:border-red-800/40 text-red-700 dark:text-red-400',
                        'Finishes': 'border-violet-300 dark:border-violet-800/40 text-violet-700 dark:text-violet-400',
                        'Flooring': 'border-violet-300 dark:border-violet-800/40 text-violet-700 dark:text-violet-400',
                        'Painting': 'border-violet-300 dark:border-violet-800/40 text-violet-700 dark:text-violet-400',
                        'Ceilings': 'border-violet-300 dark:border-violet-800/40 text-violet-700 dark:text-violet-400',
                      };
                      const tradeIcons = {
                        'HVAC': Wrench,
                        'Electrical': Sparkles,
                        'Plumbing': Layers,
                        'Fire Protection': Shield,
                        'Finishes': Building2,
                        'Flooring': Building2,
                        'Painting': Building2,
                        'Ceilings': Building2,
                      };
                      const colorClass = tradeColors[div.trade] || 'border-border/40 text-muted-foreground';
                      const IconComp = tradeIcons[div.trade] || Building2;
                      const maxBar = Math.max(...results.data.division_breakdown.map(d => d.unique_types));
                      const barWidth = maxBar > 0 ? Math.max((div.unique_types / maxBar) * 100, 8) : 8;
                      return (
                        <div key={i} className={`relative px-3 py-2 bg-muted/50 border rounded-sm ${colorClass.split(' ')[0]} overflow-hidden`}>
                          <div className="absolute inset-y-0 left-0 bg-current opacity-[0.04]" style={{ width: `${barWidth}%` }} />
                          <div className="relative flex items-center gap-2">
                            <IconComp className={`w-3.5 h-3.5 flex-shrink-0 ${colorClass.split(' ')[1]}`} />
                            <span className={`text-xs font-medium flex-shrink-0 ${colorClass.split(' ')[1]}`}>
                              {div.trade}
                            </span>
                            <span className="text-[11px] font-mono font-bold text-foreground/80 ml-auto flex-shrink-0">
                              {div.unique_types} type{div.unique_types !== 1 ? 's' : ''}
                              {div.total_items > div.unique_types && (
                                <span className="text-muted-foreground font-normal ml-1">({div.total_items} total)</span>
                              )}
                            </span>
                          </div>
                          {div.examples?.length > 0 && (
                            <p className="text-[10px] text-muted-foreground mt-0.5 pl-5 truncate">
                              {div.examples.join(', ')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {results.data.message || 'Equipment assets are now in the Assets & Equipment tab.'}
              </p>
            </div>
          )}

          {/* Change Order Results */}
          {results.type === 'changeorder' && (
            <div className="p-4 bg-card border border-rose-300 dark:border-rose-900/30 rounded-sm space-y-3">
              <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-rose-700 dark:text-rose-400">
                <CheckCircle2 className="w-4 h-4" />
                Change Orders / VE Log Parsed
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-rose-600 dark:text-rose-400">{results.data.change_orders_parsed}</p>
                  <p className="text-[10px] text-muted-foreground">COs Parsed</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-primary">{results.data.equipment_changes_found}</p>
                  <p className="text-[10px] text-muted-foreground">Equipment Changes</p>
                </div>
                <div className="text-center p-2 bg-secondary/30 rounded-sm">
                  <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">{results.data.equipment_changes_applied}</p>
                  <p className="text-[10px] text-muted-foreground">Changes Applied</p>
                </div>
              </div>
              {results.data.equipment_changes?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-rose-500">Equipment Impacts</p>
                  {results.data.equipment_changes.map((c, i) => (
                    <div key={i} className="px-3 py-2 bg-rose-50 dark:bg-rose-950/10 border border-rose-200 dark:border-rose-900/20 rounded-sm text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowRightLeft className="w-3 h-3 text-rose-500" />
                        <span className="font-medium text-foreground/80">CO #{c.co_number} — {c.impact_type}</span>
                        <span className="text-[10px] text-muted-foreground">{c.co_date}</span>
                      </div>
                      {c.original_equipment && <p className="text-muted-foreground pl-5">From: {c.original_equipment}</p>}
                      {c.new_equipment && <p className="text-emerald-500 pl-5">To: {c.new_equipment}</p>}
                      <p className="text-muted-foreground pl-5">{c.co_description}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {results.data.change_orders?.map((co, i) => (
                  <div key={i} className={`flex items-center gap-3 px-2 py-1.5 text-xs rounded-sm border ${
                    co.equipment_impact ? 'bg-rose-50 dark:bg-rose-950/10 border-rose-200 dark:border-rose-900/20' : 'bg-secondary/20 border-border/30'
                  }`}>
                    <span className="font-mono text-muted-foreground w-14 shrink-0">CO {co.co_number}</span>
                    <span className={`flex-1 truncate ${co.equipment_impact ? 'text-rose-700 dark:text-rose-300' : 'text-foreground/80'}`}>
                      {co.description}
                    </span>
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${
                      co.status === 'approved' ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400' : 'bg-secondary text-muted-foreground'
                    }`}>{co.status}</span>
                    {co.equipment_impact && (
                      <span className="text-[9px] font-mono px-1.5 py-0.5 bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-900/30 rounded-sm">
                        {co.equipment_impact.impact_type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={resetState}
            className="text-xs text-muted-foreground hover:text-muted-foreground transition-colors">
            Upload another document
          </button>
        </div>
      )}

      {/* How it Works */}
      {!identification && !results && !processing && (
        <div className="mt-6 p-4 bg-card border border-border rounded-sm">
          <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-3">How Smart Intake Works</p>
          <div className="grid grid-cols-4 gap-4">
            {[
              { step: '1', title: 'Upload Anything', desc: 'Sub lists, RFI logs, specs, submittals, equipment schedules — any project document', icon: Upload },
              { step: '2', title: 'AI Identifies', desc: 'GPT-4o reads the document and determines what type it is and what data it contains', icon: FileSearch },
              { step: '3', title: 'Auto-Parse', desc: 'The right parser extracts structured data — trades, contacts, equipment changes, CSI codes', icon: Sparkles },
              { step: '4', title: 'Populate Registry', desc: 'Creates divisions, stakeholders, assets, and tracks VE changes automatically', icon: Building2 },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center mx-auto mb-2">
                  <s.icon className="w-4 h-4 text-primary" />
                </div>
                <p className="text-[10px] font-heading uppercase tracking-wider text-foreground/80 mb-0.5">{s.title}</p>
                <p className="text-[9px] text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Timeline Activity */}
      <TimelineSummaryCompact projectId={projectId} />
    </div>
  );
}
